import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import inventoryApi from '../api/inventory.api'
import { QueryKeys } from './querykeys'

type QueryParams = Record<string, string | number | boolean | undefined>
type MutationPayload = Record<string, unknown>

const normalizeList = (payload: any) => {
  if (Array.isArray(payload)) {
    return {
      rows: payload,
      total: payload.length,
      raw: payload
    }
  }

  if (!payload || typeof payload !== 'object') {
    return {
      rows: [],
      total: 0,
      raw: payload
    }
  }

  const root = payload as Record<string, unknown>
  const data = (root.data ?? root) as Record<string, unknown>

  const directCandidates = [
    root.items,
    root.rows,
    root.results,
    root.movements,
    root.stock,
    root.warehouses,
    root.components
  ]
  const nestedCandidates = [
    data?.items,
    data?.rows,
    data?.results,
    data?.movements,
    data?.stock,
    data?.warehouses,
    data?.components,
    data?.data
  ]

  const rows =
    directCandidates.find(Array.isArray) ??
    nestedCandidates.find(Array.isArray) ??
    (Array.isArray(root.data) ? root.data : null) ??
    []

  const total =
    Number(root.count ?? root.total ?? data?.count ?? data?.total ?? (rows as unknown[]).length) || 0

  return {
    rows: rows as unknown[],
    total,
    raw: payload
  }
}

const normalizeRecipe = (payload: any) => {
  if (!payload || typeof payload !== 'object') return payload ?? null
  const root = payload as Record<string, unknown>
  const data = root.data as Record<string, unknown> | undefined

  if (data?.recipe && typeof data.recipe === 'object') return data.recipe
  if (root.recipe && typeof root.recipe === 'object') return root.recipe
  if (data && typeof data === 'object') return data
  return root
}

export const useQueryInventoryWarehouses = (businessId?: number, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [QueryKeys.GET_INVENTORY_WAREHOUSES, businessId],
    enabled: (options?.enabled ?? true) && !!businessId,
    queryFn: async () => {
      const res = await inventoryApi.getWarehouses(Number(businessId))
      return normalizeList(res.data)
    }
  })

export const useQueryInventoryItems = (
  businessId?: number,
  params: QueryParams = {},
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: [QueryKeys.GET_INVENTORY_ITEMS, businessId, params],
    enabled: (options?.enabled ?? true) && !!businessId,
    queryFn: async () => {
      const res = await inventoryApi.getItems(Number(businessId), params)
      return normalizeList(res.data)
    }
  })

export const useQueryInventoryStock = (
  businessId?: number,
  params: QueryParams = {},
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: [QueryKeys.GET_INVENTORY_STOCK, businessId, params],
    enabled: (options?.enabled ?? true) && !!businessId,
    queryFn: async () => {
      const res = await inventoryApi.getStock(Number(businessId), params)
      return normalizeList(res.data)
    }
  })

export const useQueryInventoryMovements = (
  businessId?: number,
  params: QueryParams = {},
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: [QueryKeys.GET_INVENTORY_MOVEMENTS, businessId, params],
    enabled: (options?.enabled ?? true) && !!businessId,
    queryFn: async () => {
      const res = await inventoryApi.getMovements(Number(businessId), params)
      return normalizeList(res.data)
    }
  })

export const useQueryInventoryRecipeByItem = (
  businessId?: number,
  itemId?: number,
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: [QueryKeys.GET_INVENTORY_RECIPE_BY_ITEM, businessId, itemId],
    enabled: (options?.enabled ?? true) && !!businessId && !!itemId,
    queryFn: async () => {
      try {
        const res = await inventoryApi.getRecipeByItem(Number(businessId), Number(itemId))
        return normalizeRecipe(res.data)
      } catch (error: any) {
        if (error?.response?.status === 404) return null
        throw error
      }
    }
  })

export const useQueryInventoryRecipesByItems = (
  businessId?: number,
  itemIds: number[] = [],
  options?: { enabled?: boolean }
) =>
  useQueries({
    queries: itemIds.map(itemId => ({
      queryKey: [QueryKeys.GET_INVENTORY_RECIPE_BY_ITEM, businessId, itemId],
      enabled: (options?.enabled ?? true) && !!businessId && !!itemId,
      queryFn: async () => {
        try {
          const res = await inventoryApi.getRecipeByItem(Number(businessId), Number(itemId))
          return normalizeRecipe(res.data)
        } catch (error: any) {
          if (error?.response?.status === 404) return null
          throw error
        }
      }
    }))
  })

export const useQueryInventoryRecipeComponents = (
  businessId?: number,
  recipeId?: number,
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: [QueryKeys.GET_INVENTORY_RECIPE_COMPONENTS, businessId, recipeId],
    enabled: (options?.enabled ?? true) && !!businessId && !!recipeId,
    queryFn: async () => {
      const res = await inventoryApi.getRecipeComponents(Number(businessId), Number(recipeId))
      return normalizeList(res.data)
    }
  })

export const useMutationCreateInventoryWarehouse = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ businessId, payload }: { businessId: number; payload: MutationPayload }) =>
      inventoryApi.createWarehouse(businessId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_WAREHOUSES, variables.businessId]
      })
    }
  })
}

export const useMutationDeleteInventoryWarehouse = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ businessId, warehouseId }: { businessId: number; warehouseId: number }) =>
      inventoryApi.deleteWarehouse(businessId, warehouseId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_WAREHOUSES, variables.businessId]
      })
    }
  })
}

export const useMutationUpdateInventoryWarehouse = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      businessId,
      warehouseId,
      payload
    }: {
      businessId: number
      warehouseId: number
      payload: MutationPayload
    }) => inventoryApi.updateWarehouse(businessId, warehouseId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_WAREHOUSES, variables.businessId]
      })
    }
  })
}

export const useMutationCreateInventoryItem = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ businessId, payload }: { businessId: number; payload: MutationPayload }) =>
      inventoryApi.createItem(businessId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_ITEMS, variables.businessId]
      })
    }
  })
}

export const useMutationUpdateInventoryItem = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      businessId,
      itemId,
      payload
    }: {
      businessId: number
      itemId: number
      payload: MutationPayload
    }) => inventoryApi.updateItem(businessId, itemId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_ITEMS, variables.businessId]
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_STOCK, variables.businessId]
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_MOVEMENTS, variables.businessId]
      })
    }
  })
}

export const useMutationDeleteInventoryItem = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ businessId, itemId }: { businessId: number; itemId: number }) =>
      inventoryApi.deleteItem(businessId, itemId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_ITEMS, variables.businessId]
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_STOCK, variables.businessId]
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_MOVEMENTS, variables.businessId]
      })
    }
  })
}

export const useMutationCreateInventoryManualMovement = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ businessId, payload }: { businessId: number; payload: MutationPayload }) =>
      inventoryApi.createManualMovement(businessId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_STOCK, variables.businessId]
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_MOVEMENTS, variables.businessId]
      })
    }
  })
}

export const useMutationCreateInventoryTransfer = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ businessId, payload }: { businessId: number; payload: MutationPayload }) =>
      inventoryApi.createTransfer(businessId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_STOCK, variables.businessId]
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_MOVEMENTS, variables.businessId]
      })
    }
  })
}

export const useMutationCreateInventoryRecipe = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      businessId,
      itemId,
      payload
    }: {
      businessId: number
      itemId: number
      payload: MutationPayload
    }) => inventoryApi.createRecipe(businessId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_RECIPE_BY_ITEM, variables.businessId, variables.itemId]
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_RECIPE_COMPONENTS, variables.businessId]
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_ITEMS, variables.businessId]
      })
    }
  })
}

export const useMutationUploadInventoryItems = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ businessId, file }: { businessId: number; file: File }) =>
      inventoryApi.uploadItems(businessId, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_ITEMS, variables.businessId]
      })
    }
  })
}

export const useMutationUploadInventoryInitialStock = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      businessId,
      file,
      warehouseId,
      warehouseCode
    }: {
      businessId: number
      file: File
      warehouseId?: number | string
      warehouseCode?: string
    }) => inventoryApi.uploadInitialStock(businessId, file, { warehouseId, warehouseCode }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_STOCK, variables.businessId]
      })
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_INVENTORY_MOVEMENTS, variables.businessId]
      })
    }
  })
}
